import { authedFetch, authedFetchRaw } from "../lib/api";
import type { FeedbackItem, FeedbackQueue, FeedbackSource } from "./types";

export class FeedbackApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function requireOk(response: Response): Promise<Response> {
  if (response.ok) return response;
  let detail = `Request failed (HTTP ${response.status})`;
  try {
    const body = await response.json() as { detail?: string };
    if (body.detail) detail = body.detail;
  } catch {
    // The status still carries the failure when an intermediary returned text.
  }
  throw new FeedbackApiError(response.status, detail);
}

export async function loadSources(): Promise<FeedbackSource[]> {
  const response = await requireOk(await authedFetch("/api/feedback/sources"));
  return ((await response.json()) as { sources: FeedbackSource[] }).sources;
}

export async function loadQueue(sourceId: string): Promise<FeedbackQueue> {
  const response = await requireOk(await authedFetch(`/api/feedback/${encodeURIComponent(sourceId)}`));
  return response.json() as Promise<FeedbackQueue>;
}

export async function setResponse(sourceId: string, itemId: string, value: string | null, reason?: string): Promise<FeedbackItem> {
  const response = await requireOk(await authedFetch(
    `/api/feedback/${encodeURIComponent(sourceId)}/items/${encodeURIComponent(itemId)}`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ value: value ?? null, reason: reason?.trim() || null }) },
  ));
  return response.json() as Promise<FeedbackItem>;
}

export async function clearResponse(sourceId: string, itemId: string): Promise<void> {
  await requireOk(await authedFetch(
    `/api/feedback/${encodeURIComponent(sourceId)}/items/${encodeURIComponent(itemId)}`,
    { method: "DELETE" },
  ));
}

export async function fetchExport(sourceId: string): Promise<{ blob: Blob; filename: string }> {
  const response = await requireOk(await authedFetchRaw(`/api/feedback/${encodeURIComponent(sourceId)}/export`));
  const disposition = response.headers.get("content-disposition") ?? "";
  const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? `${sourceId}-feedback`;
  return { blob: await response.blob(), filename };
}
